import { computed, ref, watch } from 'vue';
import type { OnboardingPath, StepId } from '@/types/whatsapp-onboarding';

const STORAGE_KEY_PREFIX = 'whatsapp_onboarding_path:';

function getStepsForPath(path: OnboardingPath | null): StepId[] {
  if (!path) {
    return ['intro', 'situation'];
  }

  switch (path) {
    case 'none':
      return ['intro', 'situation', 'number_choice', 'access_checklist'];
    case 'other_bsp':
      return ['intro', 'situation', 'migration_warning', 'meta_tips', 'access_checklist'];
    case 'wa_app':
      return ['intro', 'situation', 'migration_prep', 'access_checklist'];
    default:
      return ['intro', 'situation'];
  }
}

export function useWhatsappOnboardingWizard(shopId: number) {
  const path = ref<OnboardingPath | null>(null);
  const stepIndex = ref(0);

  const storageKey = `${STORAGE_KEY_PREFIX}${shopId}`;

  const restoreFromStorage = () => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored === 'none' || stored === 'other_bsp' || stored === 'wa_app') {
        path.value = stored;
      }
    } catch {
      // ignore
    }
  };

  restoreFromStorage();

  watch(path, (value) => {
    try {
      if (value) {
        localStorage.setItem(storageKey, value);
      } else {
        localStorage.removeItem(storageKey);
      }
    } catch {
      // ignore
    }
  });

  const steps = computed(() => getStepsForPath(path.value));

  const currentStepId = computed(() => steps.value[stepIndex.value] ?? 'intro');

  const isFirstStep = computed(() => stepIndex.value === 0);

  const isLastStep = computed(() => stepIndex.value === steps.value.length - 1);

  const isConnectStep = computed(() => {
    const id = currentStepId.value;
    return id === 'access_checklist';
  });

  const canGoNext = computed(() => {
    const id = currentStepId.value;
    if (id === 'situation') {
      return path.value !== null;
    }
    return !isLastStep.value;
  });

  const setPath = (value: OnboardingPath) => {
    path.value = value;
  };

  const goToStepId = (stepId: StepId) => {
    const idx = steps.value.indexOf(stepId);
    if (idx >= 0) {
      stepIndex.value = idx;
    }
  };

  const switchToNewNumberPath = () => {
    path.value = 'none';
    goToStepId('number_choice');
  };

  const goNext = () => {
    if (!canGoNext.value || isLastStep.value) {
      return;
    }
    stepIndex.value += 1;
  };

  const goBack = () => {
    if (isFirstStep.value) {
      return;
    }
    stepIndex.value -= 1;
  };

  const reset = () => {
    path.value = null;
    stepIndex.value = 0;
    try {
      localStorage.removeItem(storageKey);
    } catch {
      // ignore
    }
  };

  const hasProgress = computed(() => stepIndex.value > 0 || path.value !== null);

  const stepNumber = computed(() => stepIndex.value + 1);

  const totalSteps = computed(() => steps.value.length);

  const isIntroStep = computed(() => currentStepId.value === 'intro');

  return {
    path,
    stepIndex,
    steps,
    currentStepId,
    isFirstStep,
    isLastStep,
    isConnectStep,
    isIntroStep,
    canGoNext,
    stepNumber,
    totalSteps,
    setPath,
    goNext,
    goBack,
    goToStepId,
    switchToNewNumberPath,
    reset,
    hasProgress,
  };
}
